import * as React from "react";
import { ArrowLeft, ChevronDown, Group, MoreHorizontal, Users } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { DocumentGroupCreateModal } from "@/modules/documentGroup/createModal";
import { type FolderItem } from "@/types/fse";
import { DriveHeaderActions } from "@/views/drive/components/driveHeaderActions";
import { DriveRowMenu } from "@/views/drive/components/driveRowMenu";
import { DriveWorkspaceHeader } from "@/views/drive/workspace/DriveWorkspaceHeader";
import type { SupplierContractFolderContext } from "@/views/drive/useSupplierContractFolder";

interface DriveHeaderProps {
  parentFolder: FolderItem | null;
  currentFolder: FolderItem;
  isRootFolder: boolean;
  askAIButton?: boolean;
  addFilesButton?: boolean;
  /** Present on a Supplier contract folder: the back link and the contract summary action. */
  contractFolder?: SupplierContractFolderContext | null;
  canCreateContractSummary?: boolean;
}

export const DriveHeader: React.FC<DriveHeaderProps> = ({
  parentFolder,
  currentFolder,
  isRootFolder,
  askAIButton,
  addFilesButton,
  contractFolder = null,
  canCreateContractSummary = false,
}) => {
  const navigate = useNavigate();
  const [isCreateGroupOpen, setIsCreateGroupOpen] = React.useState(false);
  const supplierReturnTo = contractFolder
    ? {
        pathname: `/suppliers/${encodeURIComponent(contractFolder.supplierId)}`,
        search: `?${new URLSearchParams({ familyId: contractFolder.relationId }).toString()}`,
        hash: "#contract-families",
      }
    : null;

  const onRemove = () => {
    navigate("/drive/");
  };

  const onUnwrap = () => {
    // The folder we are looking at is gone, so fall back to the one above it. Its own
    // parent_fs_id is not that folder - /ls reports the queried entry's own id there -
    // so the breadcrumb path is the only reliable source. A parent that is the root is
    // reached via /drive/, since /drive/<rootFseId> renders as a non-root directory.
    const { path } = currentFolder.current_location;
    navigate(path.length > 1 ? `/drive/${path[path.length - 1].id}` : "/drive/");
  };

  const isShared = currentFolder.access === "public" || currentFolder.permissions.length > 1;
  const menuLabel = isRootFolder ? "Actions for Documents" : `Actions for ${currentFolder.name}`;
  const folderMenu = (
    <DriveRowMenu
      isRootFolder={isRootFolder}
      isFolder
      variant="empty"
      item={currentFolder}
      onRemove={onRemove}
      onUnwrap={onUnwrap}
      currentFolderId={currentFolder.id}
      triggerAriaLabel={menuLabel}
      triggerTooltipText={isRootFolder ? menuLabel : undefined}
      additionalItems={
        isRootFolder ? (
          <DropdownMenuItem onSelect={() => setIsCreateGroupOpen(true)}>
            <Group className="mr-2 h-4 w-4" />
            Create Group
          </DropdownMenuItem>
        ) : undefined
      }
    >
      {isRootFolder ? <MoreHorizontal className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </DriveRowMenu>
  );

  return (
    <>
      {supplierReturnTo ? (
        <Link
          to={supplierReturnTo}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-950 hover:underline"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back to Contract Families
        </Link>
      ) : null}
      <DriveWorkspaceHeader
        currentFolder={currentFolder}
        parentFolder={parentFolder}
        isRootFolder={isRootFolder}
        titleActions={
          <>
            {isShared && <Users className="h-4 w-4 text-gray-500" />}
            {!isRootFolder && folderMenu}
          </>
        }
        actions={
          <>
            <DriveHeaderActions
              parentFolder={parentFolder}
              currentFolder={currentFolder}
              isRootFolder={isRootFolder}
              askAIButton={askAIButton}
              addFilesButton={addFilesButton}
              hideOverflow={isRootFolder}
              contractFolder={contractFolder}
              canCreateContractSummary={canCreateContractSummary}
            />
            {isRootFolder && folderMenu}
          </>
        }
      />
      {isCreateGroupOpen && (
        <DocumentGroupCreateModal
          parentFolderId={currentFolder.id}
          isOpen={isCreateGroupOpen}
          setIsOpen={setIsCreateGroupOpen}
        />
      )}
    </>
  );
};
